// Overview screen: company headline view, region comparison, and group team view.
// Port of the prototype's renderOverview (individual scope is routed to the worker screen).

import React from 'react';
import { cn } from '@/lib/utils';
import { scopedWorkers, useDash } from '../state';
import type { DashState } from '../state';
import {
  Banner,
  Chip,
  Dot,
  Grid,
  Kpi,
  KpiRow,
  Panel,
  Pill,
  SecHeader,
  dataTable,
  fedTone,
  toneText,
  toneVar,
} from '../ui';
import type { Tone } from '../ui';
import {
  GROUPS,
  REGIONS,
  avgFED,
  highFEDCount,
  notReadyCount,
  onShift,
  rdyLabel,
  readiness,
  regionOf,
  wArr,
  workersInGroup,
  workersInRegion,
  wtrBreaches,
} from '../engine/data';
import type { Worker } from '../engine/data';
import { pliLabel, psiLabel } from '../engine/scores';
import { pliTone, psiTone, rdyTone } from '../engine/scores';
import { genInsight } from '../engine/insights';
import { AiInsight, AiRecs } from '../AiPanels';
import { CompBar, ScoreLegend, ScoreTrio } from '../ScoreWidgets';

type CompanyTab = 'today' | '7d' | '30d' | '90d';

const TAB_CFG: Record<CompanyTab, { label: string; idx: number; window: string }> = {
  today: { label: 'Today', idx: 11, window: 'live' },
  '7d': { label: 'Last 7 Days', idx: 9, window: '7-day rolling' },
  '30d': { label: 'Last Month', idx: 7, window: '30-day rolling' },
  '90d': { label: 'Last Quarter', idx: 3, window: '90-day rolling' },
};

const mean = (ws: Worker[], f: (w: Worker) => number): number =>
  Math.round(ws.reduce((a, w) => a + f(w), 0) / ws.length);

/** FED at a fed12 slice, falling back to the live value when history is missing. */
const fedAt = (w: Worker, idx: number): number => (w.fed12?.[idx] != null ? w.fed12[idx] : w.fed);

const kpiTone = (alert: boolean, warn: boolean): Tone => (alert ? 'bad' : warn ? 'warn' : 'good');

const Small: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <small className='text-base font-normal text-muted-foreground'>{children}</small>
);

const Avatar: React.FC<{ init: string; className?: string }> = ({ init, className }) => (
  <div
    className={cn(
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-[13px] font-bold',
      className,
    )}
  >
    {init}
  </div>
);

const ScorePill: React.FC<{ label: string; value: number; tone: Tone }> = ({ label, value, tone }) => (
  <Pill tone={tone}>
    {label}&nbsp;{value}
  </Pill>
);

/** Region switcher buttons coloured by the region's own colour. */
const RegionButtons: React.FC<{ activeRid: string | null; onPick: (rid: string) => void; small?: boolean }> = ({
  activeRid,
  onPick,
  small,
}) => (
  <div className={cn('flex flex-wrap gap-2', small ? 'mb-2' : 'mb-5')}>
    {Object.entries(REGIONS).map(([rid, r]) => {
      const active = rid === activeRid;
      return (
        <button
          key={rid}
          type='button'
          onClick={() => onPick(rid)}
          className={cn(
            'rounded-full border-2 font-semibold transition-colors',
            small ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]',
            !active && 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          style={active ? { borderColor: r.color, background: `${r.color}22`, color: r.color } : undefined}
        >
          {r.name}
        </button>
      );
    })}
  </div>
);

/** Worker row card (prototype .wcard) with a readiness-coloured left edge. */
const WorkerRow: React.FC<{ w: Worker; meta: string; onOpen: () => void }> = ({ w, meta, onOpen }) => {
  const r = readiness(w);
  const tone = rdyTone(r);
  return (
    <button
      type='button'
      onClick={onOpen}
      className='flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted/60'
    >
      <span
        className={cn(
          'h-9 w-1 shrink-0 rounded-full',
          tone === 'good' ? 'bg-success' : tone === 'warn' ? 'bg-warning' : 'bg-destructive',
        )}
      />
      <Avatar init={w.init} />
      <div className='min-w-0 flex-1'>
        <div className='text-sm font-semibold'>{w.name}</div>
        <div className='text-[11px] text-muted-foreground'>{meta}</div>
      </div>
      <div className='flex flex-wrap items-center justify-end gap-2'>
        <ScoreTrio w={w} />
        <ScorePill label='PLI' value={w.pli} tone={pliTone(w.pli)} />
        <ScorePill label='PSI' value={w.psi} tone={psiTone(w.psi)} />
      </div>
      <Pill tone={tone}>{rdyLabel(r)}</Pill>
    </button>
  );
};

const PliKpi: React.FC<{ ws: Worker[] }> = ({ ws }) => {
  const avg = mean(ws, (w) => w.pli || 0);
  const hi = ws.filter((w) => w.pli >= 75).length;
  const kcal = mean(ws, (w) => w.pliKcal || 0);
  return (
    <Kpi
      label='Avg PLI — Phys Load'
      tone={kpiTone(hi > 0, avg >= 55)}
      value={<span className={toneText[pliTone(avg)]}>{avg}</span>}
      sub={`${hi > 0 ? `${hi} very high` : `${pliLabel(avg)} avg`} · ~${kcal.toLocaleString()} kcal`}
    />
  );
};

const PsiKpi: React.FC<{ ws: Worker[]; suffix?: string }> = ({ ws, suffix }) => {
  const avg = mean(ws, (w) => w.psi || 0);
  const hi = ws.filter((w) => w.psi >= 75).length;
  return (
    <Kpi
      label='Avg PSI — Psych Stress'
      tone={kpiTone(hi > 0, avg >= 55)}
      value={<span className={toneText[psiTone(avg)]}>{avg}</span>}
      sub={`${hi > 0 ? `${hi} very high` : `${psiLabel(avg)} avg`}${suffix ?? ''}`}
    />
  );
};

// ── Company scope ───────────────────────────────────────────────────────────

const CompanyView: React.FC<{ s: DashState }> = ({ s }) => {
  const d = useDash();
  const all = wArr();
  const ctab = (TAB_CFG[s.companyTab as CompanyTab] ? s.companyTab : 'today') as CompanyTab;
  const tc = TAB_CFG[ctab];
  const today = ctab === 'today';

  const notRdy = notReadyCount(all);
  const hi = highFEDCount(all);
  const wtr = wtrBreaches(all);
  const avg = avgFED(all);
  const onShiftNow = all.filter(onShift);

  const avgCDI = mean(all, (w) => w.cdi);
  const highCDI = all.filter((w) => w.cdi >= 60).length;
  const critLRM = all.filter((w) => w.lrm > 40).length;

  const periodFED = today ? avg : mean(all, (w) => fedAt(w, tc.idx));
  const periodHi = today ? hi : all.filter((w) => fedAt(w, tc.idx) >= 80).length;
  const atRisk = today ? notRdy : all.filter((w) => fedAt(w, tc.idx) >= 75).length;

  const topRisk = [...all].sort((a, b) => b.fed + Math.max(0, b.lrm) - (a.fed + Math.max(0, a.lrm))).slice(0, 3);
  const heat = [...all].sort((a, b) => b.fed - a.fed);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center gap-2'>
        {(Object.keys(TAB_CFG) as CompanyTab[]).map((t) => (
          <Chip key={t} active={ctab === t} onClick={() => d.patch({ companyTab: t })}>
            {TAB_CFG[t].label}
          </Chip>
        ))}
        <span className='ml-1 text-[11px] text-muted-foreground'>
          {tc.window} · {all.length} workers · {Object.keys(REGIONS).length} regions
        </span>
      </div>

      <KpiRow>
        <Kpi
          label={today ? 'Not Ready Today' : `At Risk — ${tc.label}`}
          tone={today && notRdy > 0 ? 'bad' : 'good'}
          value={
            <>
              {atRisk}
              <Small>/{all.length}</Small>
            </>
          }
          sub={today ? (notRdy > 0 ? 'Immediate action required' : 'All workforce clear') : 'FED ≥75 in period'}
        />
        <Kpi
          label='Critical FED (≥80)'
          tone={periodHi > 0 ? 'bad' : 'good'}
          value={periodHi}
          sub={`${tc.label} · ${periodHi > 0 ? 'Critical level' : 'No critical cases'}`}
          subTrend={periodHi > 0 ? 'up' : undefined}
        />
        <Kpi
          label='High CDI (≥60)'
          tone={kpiTone(highCDI > 0, avgCDI >= 35)}
          value={highCDI}
          sub={`Avg CDI: ${avgCDI} · Circadian disruption`}
        />
        <Kpi
          label='Critical LRM (>40)'
          tone={critLRM > 0 ? 'bad' : 'good'}
          value={critLRM}
          sub='State vs shift demand mismatch'
        />
        <Kpi
          label='WTR Violations'
          tone={wtr > 0 ? 'bad' : 'good'}
          value={wtr}
          sub={wtr > 0 ? 'Rest period breaches' : 'Fully compliant'}
          subTrend={wtr > 0 ? 'up' : undefined}
        />
        <Kpi
          label={`Avg FED — ${tc.label}`}
          tone={kpiTone(periodFED >= 70, periodFED >= 55)}
          value={periodFED}
          sub={`${tc.window} · ${all.length} workers`}
        />
        <PliKpi ws={all} />
        <PsiKpi ws={all} suffix=' · company-wide' />
      </KpiRow>

      <ScoreLegend />
      <AiInsight>{genInsight('overview', all, { isCompany: true, scopeName: 'the company' })}</AiInsight>
      <AiRecs workers={all} opts={{ isCompany: true, scopeName: 'the company' }} scopeLabel='the company' />

      <Grid cols={2}>
        <Panel title='Region Performance' sub={`Avg FED · PLI · Not Ready · WTR — ${tc.label} — click to drill in`}>
          <div className='flex flex-col gap-3.5'>
            {Object.entries(REGIONS).map(([rid, reg]) => {
              const rw = workersInRegion(rid);
              const rFED = today ? avgFED(rw) : mean(rw, (w) => fedAt(w, tc.idx));
              const rNR = notReadyCount(rw);
              const rWTR = wtrBreaches(rw);
              const rPLI = mean(rw, (w) => w.pli || 0);
              return (
                <button
                  key={rid}
                  type='button'
                  onClick={() => d.drillRegion(rid)}
                  className='rounded-xl border border-border bg-muted/40 p-3 text-left transition-opacity hover:opacity-80'
                >
                  <div className='mb-2 flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                      <Dot color={reg.color} className='h-2.5 w-2.5' />
                      <span className='text-sm font-semibold'>{reg.name}</span>
                      <span className='text-[11px] text-muted-foreground'>{rw.length} workers</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className={cn('text-[11px] font-semibold', toneText[pliTone(rPLI)])}>PLI {rPLI}</span>
                      {rNR > 0 && <Pill tone='bad'>{rNR} not ready</Pill>}
                      {rWTR > 0 && <Pill tone='warn'>{rWTR} WTR</Pill>}
                    </div>
                  </div>
                  <CompBar val={rFED} max={100} color={toneVar[fedTone(rFED)]} />
                  <div className='mt-1 text-[10px] text-muted-foreground'>Avg FED — {tc.label}</div>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel title='Highest Risk Workers' sub='Top 3 by combined FED + LRM'>
          <div className='flex flex-col gap-2.5'>
            {topRisk.map((w, i) => {
              const r = readiness(w);
              const [, reg] = regionOf(w);
              const critical = w.fed >= 80 || w.lrm > 40;
              return (
                <button
                  key={w.init}
                  type='button'
                  onClick={() => d.drillWorker(w.init)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border bg-muted/40 p-3 text-left transition-opacity hover:opacity-80',
                    critical ? 'border-destructive' : 'border-border',
                  )}
                >
                  <div className='w-5 text-center text-[22px] font-extrabold text-muted-foreground'>{i + 1}</div>
                  <Avatar init={w.init} />
                  <div className='min-w-0 flex-1'>
                    <div className='font-semibold'>{w.name}</div>
                    <div className='text-[11px] text-muted-foreground'>
                      {w.role} · {reg.name}
                    </div>
                  </div>
                  <div className='flex flex-col items-end gap-1'>
                    <ScoreTrio w={w} />
                    <Pill tone={rdyTone(r)}>{rdyLabel(r)}</Pill>
                  </div>
                </button>
              );
            })}
          </div>
          <div className='mt-3 flex items-center justify-between border-t border-border pt-2.5 text-[11px] text-muted-foreground'>
            <span>
              On shift now: <strong className='text-foreground'>{onShiftNow.length}</strong>
            </span>
            <span>
              Avg sleep deficit: <strong className='text-warning'>−1.1h</strong>
            </span>
            <button type='button' onClick={() => d.go('today')} className='text-info hover:underline'>
              Pre-shift detail →
            </button>
          </div>
        </Panel>
      </Grid>

      <div>
        <SecHeader>
          Workforce FED Heatmap
          <span className='text-[11px] font-normal'>— last 4 weeks, sorted by risk</span>
        </SecHeader>
        <Panel>
          <div className={dataTable.wrap}>
            <table className={dataTable.table}>
              <thead>
                <tr>
                  <th className={dataTable.th}>Worker</th>
                  <th className={dataTable.th}>Region</th>
                  <th className={dataTable.th}>Role</th>
                  {['W1', 'W2', 'W3', 'W4', 'Now'].map((h) => (
                    <th key={h} className={cn(dataTable.th, 'text-center')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heat.map((w) => {
                  const [, reg] = regionOf(w);
                  const vals = [w.fed12[0], w.fed12[3], w.fed12[6], w.fed12[9]];
                  return (
                    <tr
                      key={w.init}
                      className={cn(dataTable.tr, 'cursor-pointer')}
                      onClick={() => d.drillWorker(w.init)}
                    >
                      <td className={cn(dataTable.td, 'font-medium')}>{w.name}</td>
                      <td className={dataTable.td}>
                        <span
                          className='rounded-full px-1.5 py-0.5 text-[10px] font-semibold'
                          style={{ background: `${reg.color}22`, color: reg.color }}
                        >
                          {reg.name}
                        </span>
                      </td>
                      <td className={cn(dataTable.td, 'text-[11px] text-muted-foreground')}>{GROUPS[w.group].name}</td>
                      {vals.map((v, i) => (
                        <td key={i} className={cn(dataTable.td, 'text-center')}>
                          <Pill tone={fedTone(v)} className='min-w-8 justify-center border-transparent'>
                            {v}
                          </Pill>
                        </td>
                      ))}
                      <td className={cn(dataTable.td, 'text-center')}>
                        <Pill tone={fedTone(w.fed)} className='min-w-8 justify-center border-transparent font-bold'>
                          {w.fed}
                        </Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
};

// ── Region scope ────────────────────────────────────────────────────────────

const RegionView: React.FC<{ s: DashState }> = ({ s }) => {
  const d = useDash();
  const rid0 = s.activeRegion as string;
  const reg = REGIONS[rid0];
  const rw = workersInRegion(rid0);
  const notRdy = notReadyCount(rw);
  const hi = highFEDCount(rw);
  const wtr = wtrBreaches(rw);
  const avg = avgFED(rw);
  const rAvgCDI = mean(rw, (w) => w.cdi);
  const rCritLRM = rw.filter((w) => w.lrm > 40).length;
  const sorted = [...rw].sort((a, b) => b.lrm - a.lrm || b.fed - a.fed);

  return (
    <div className='flex flex-col gap-4'>
      <RegionButtons activeRid={rid0} onPick={(rid) => d.patch({ activeRegion: rid, screen: 'overview' })} />

      <KpiRow>
        <Kpi
          label='Not Ready'
          tone={notRdy > 0 ? 'bad' : 'good'}
          value={
            <>
              {notRdy}
              <Small>/{rw.length}</Small>
            </>
          }
          sub={reg.name}
        />
        <Kpi
          label='Critical FED (≥80)'
          tone={hi > 0 ? 'bad' : 'good'}
          value={hi}
          sub='In this region'
          subTrend={hi > 0 ? 'up' : undefined}
        />
        <Kpi label='Avg CDI' tone={kpiTone(rAvgCDI >= 60, rAvgCDI >= 35)} value={rAvgCDI} sub='Circadian disruption' />
        <Kpi label='Critical LRM' tone={rCritLRM > 0 ? 'bad' : 'good'} value={rCritLRM} sub='High demand mismatch' />
        <Kpi label='WTR Violations' tone={wtr > 0 ? 'bad' : 'good'} value={wtr} sub='Rest breaches' />
        <Kpi label='Avg FED' tone={kpiTone(avg >= 70, avg >= 55)} value={avg} sub={`vs company ${avgFED(wArr())}`} />
        <PliKpi ws={rw} />
        <PsiKpi ws={rw} />
      </KpiRow>

      <AiInsight>{genInsight('overview', rw, { regionId: rid0, scopeName: reg.name })}</AiInsight>
      <AiRecs workers={rw} opts={{ regionId: rid0, scopeName: reg.name }} scopeLabel={reg.name} />

      <Grid cols={2}>
        <Panel title='All Regions Compared' sub='Avg FED — select to view'>
          <div className='flex flex-col gap-3'>
            {Object.entries(REGIONS).map(([rid, r]) => {
              const w = workersInRegion(rid);
              const nr = notReadyCount(w);
              const active = rid === rid0;
              return (
                <button
                  key={rid}
                  type='button'
                  onClick={() => d.patch({ activeRegion: rid, screen: 'overview' })}
                  className={cn('rounded-xl border-2 p-3 text-left', !active && 'border-border bg-muted/40')}
                  style={active ? { borderColor: r.color, background: `${r.color}11` } : undefined}
                >
                  <div className='mb-1.5 flex items-center justify-between'>
                    <span className='font-semibold' style={active ? { color: r.color } : undefined}>
                      {r.name}
                    </span>
                    <span className='text-[11px] text-muted-foreground'>
                      {w.length} workers{' '}
                      {nr > 0 && (
                        <>
                          · <span className='text-destructive'>{nr} not ready</span>
                        </>
                      )}
                    </span>
                  </div>
                  <CompBar val={avgFED(w)} max={100} color={r.color} />
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel title={`Groups in ${reg.name}`} sub='Click a group to drill in'>
          <div className='flex flex-col gap-2.5'>
            {reg.groups.map((gid) => {
              const gw = workersInGroup(gid);
              const gFED = avgFED(gw);
              const gNR = notReadyCount(gw);
              const gWTR = wtrBreaches(gw);
              return (
                <button
                  key={gid}
                  type='button'
                  onClick={() => d.patch({ activeGroup: gid, drillScope: 'group', screen: 'overview' })}
                  className='flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 text-left transition-colors hover:bg-muted'
                >
                  <div className='min-w-0 flex-1'>
                    <div className='text-[13px] font-semibold'>{GROUPS[gid].name}</div>
                    <div className='text-[11px] text-muted-foreground'>
                      {gw.length} worker{gw.length !== 1 ? 's' : ''}
                      {gNR > 0 && (
                        <>
                          {' '}
                          · <span className='text-destructive'>{gNR} not ready</span>
                        </>
                      )}
                      {gWTR > 0 && (
                        <>
                          {' '}
                          · <span className='text-warning'>{gWTR} WTR</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className='text-right'>
                    <div className={cn('text-xl font-bold', toneText[fedTone(gFED)])}>{gFED}</div>
                    <div className='text-[10px] text-muted-foreground'>Avg FED</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>
      </Grid>

      <div>
        <SecHeader>
          {reg.name} — Worker List <span className='text-[11px] font-normal'>sorted by risk</span>
        </SecHeader>
        <div className='flex flex-col gap-2'>
          {sorted.map((w) => (
            <WorkerRow
              key={w.init}
              w={w}
              meta={`${w.role} · ${GROUPS[w.group].name}`}
              onOpen={() => d.drillWorker(w.init)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Group scope ─────────────────────────────────────────────────────────────

const GroupView: React.FC<{ s: DashState }> = ({ s }) => {
  const d = useDash();
  const gid0 = s.activeGroup as number;
  const grp = GROUPS[gid0];
  const grpRegion = grp.region;
  const gw = workersInGroup(gid0);
  const notRdy = notReadyCount(gw);
  const hi = highFEDCount(gw);
  const wtr = wtrBreaches(gw);
  const avg = avgFED(gw);
  const gAvgCDI = mean(gw, (w) => w.cdi);
  const gCritLRM = gw.filter((w) => w.lrm > 40).length;
  const sorted = [...gw].sort((a, b) => b.lrm - a.lrm || b.fed - a.fed);

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <RegionButtons
          small
          activeRid={grpRegion}
          onPick={(rid) => d.patch({ activeRegion: rid, activeGroup: REGIONS[rid].groups[0], screen: 'overview' })}
        />
        <div className='flex flex-wrap gap-1.5'>
          {REGIONS[grpRegion].groups.map((gid) => (
            <Chip key={gid} active={gid === gid0} onClick={() => d.patch({ activeGroup: gid, screen: 'overview' })}>
              {GROUPS[gid].name}
            </Chip>
          ))}
        </div>
      </div>

      <KpiRow>
        <Kpi
          label='Not Ready'
          tone={notRdy > 0 ? 'bad' : 'good'}
          value={
            <>
              {notRdy}
              <Small>/{gw.length}</Small>
            </>
          }
          sub={grp.name}
        />
        <Kpi
          label='Critical FED (≥80)'
          tone={hi > 0 ? 'bad' : 'good'}
          value={hi}
          sub='In this group'
          subTrend={hi > 0 ? 'up' : undefined}
        />
        <Kpi label='Avg CDI' tone={kpiTone(gAvgCDI >= 60, gAvgCDI >= 35)} value={gAvgCDI} sub='Circadian disruption' />
        <Kpi label='Critical LRM' tone={gCritLRM > 0 ? 'bad' : 'good'} value={gCritLRM} sub='Demand mismatch' />
        <Kpi label='WTR Violations' tone={wtr > 0 ? 'bad' : 'good'} value={wtr} sub='Rest breaches' />
        <Kpi
          label='Group Avg FED'
          tone={kpiTone(avg >= 70, avg >= 55)}
          value={avg}
          sub={`vs region avg ${avgFED(workersInRegion(grpRegion))}`}
        />
        <PliKpi ws={gw} />
        <PsiKpi ws={gw} />
      </KpiRow>

      <AiInsight>{genInsight('overview', gw, { scopeName: grp.name })}</AiInsight>
      <AiRecs workers={gw} opts={{ scopeName: grp.name }} scopeLabel={grp.name} />

      <div>
        <SecHeader>
          {grp.name} — Team Members{' '}
          <span className='text-[11px] font-normal'>sorted by LRM · click to view individual</span>
        </SecHeader>
        <div className='flex flex-col gap-2'>
          {sorted.map((w) => (
            <WorkerRow
              key={w.init}
              w={w}
              meta={w.role}
              onOpen={() => d.patch({ activeWorker: w.init, drillScope: 'individual', screen: 'worker' })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const OverviewScreen: React.FC = () => {
  const state = useDash();
  // scopedWorkers guards against an empty scope (e.g. group with no members).
  if (!scopedWorkers(state).length) return <Banner tone='muted'>No workers in this scope.</Banner>;
  if (state.drillScope === 'region' && state.activeRegion) return <RegionView s={state} />;
  if (state.drillScope === 'group' && state.activeGroup !== null) return <GroupView s={state} />;
  // Individual scope is routed to the worker screen by the state layer.
  if (state.drillScope === 'individual') return null;
  return <CompanyView s={state} />;
};
